import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_MONDO_TRACKING_APP, EnableMondoTrackingAppInput } from '../data/mutations'

/**
 * Saves MondoTracking app's API key.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const MondoTrackingModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableMondoTrackingApp, { loading }] = useMutation<
    { enableMondoTrackingApp: { appEnabled: boolean } },
    EnableMondoTrackingAppInput
  >(ENABLE_MONDO_TRACKING_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Mondo Tracking app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect API key') {
        setFormError('Invalid API key. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid API key. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { apiKey: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableMondoTrackingApp({
      variables: {
        apiKey: event.apiKey,
        appType: appType
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/verizon-connect"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable Mondo-Tracking for Chaine`}
        subtitle={`Enabling this integration requires an admin Mondo-Tracking account. Enter your API key.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Mondo-Tracking API Key',
            name: 'apiKey',

            placeHolder: 'Enter your Mondo-Tracking API Key',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

MondoTrackingModal.displayName = 'MondoTrackingModal'

export { MondoTrackingModal }
